import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useScreenDetector } from '../../custom-hooks/screen-detector';
import {
  getAmountDisplay,
  getDiscountDisplay,
  calculateDiscountValue,
} from '../../helpers/discount';
import { roundOff, roundTo2String } from '../../helpers/float';

import TileLayout from './Layouts/Tile';
import { getPriceDetailStyles } from '..';
import { logger } from '../../../../common/logger';
import { reloadPage } from '../../../../pageHelper';

export default function VD(props) {
  const {
    mrOfferID,
    primary,
    vdOptions,
    isVDMultipleVariantsSelectable,
    discountType,
    currencyFormat,
    currencySymbol,
    decimalSymbol,
    thousandSeparator,
    isAdmin,
    isAdminMobileTab,
    getVDVariantsToAdd,
    addOfferedVariantsToCart,
    goToCart,
    addToCartSuccessEvent,
    priceRoundType,
    discountRoundType,
    selectedVariants,
    setSelectedVariants,
    selectedVDOptionIndex,
    setSelectedVDOptionIndex,
    totalDisplayValues,
    setTotalDiplayValues,
    updateIsVDVariantSelectorInViewForAboveATC,
    updateSelectedVDOption,
    updateVDOfferTheme,
    isVDQuantityOptionSelected,
    vdSelectedQuantityOptionIndex,
    widgetID,
    ...restProps
  } = props;

  const {
    mobileLayoutType,
    desktopLayoutType,
    widgetPosition,
    vdShowVariantSelector,
    productDiscountText,
    vdShowPriceForEachItem,
    isProductButtonActionGoToCart,
    vdQuantitySelectorTitleText,
    vdVariantSecTitleTextForMultiple,
    vdVariantSecTitleText,
    isRefreshPageAfterProductAddition,
  } = props.theme;
  const { isMobile: isMobileDevice } = useScreenDetector();

  const isMobile = isAdmin ? isAdminMobileTab : isMobileDevice;
  const isWidgetPositionAboveATC = widgetPosition === 'above_atc';

  const selectedVDOption = vdOptions[selectedVDOptionIndex];

  const getDefaultSelectedVDOptionIndex = () => {
    if (!Array.isArray(vdOptions)) return null;

    if (vdSelectedQuantityOptionIndex >= vdOptions.length)
      return vdOptions.length - 1;
    if (vdSelectedQuantityOptionIndex < 0) return 0;

    return vdSelectedQuantityOptionIndex;
  };

  const [isATCLoading, setIsATCLoading] = useState(false);
  const [isProductAdded, setIsProductAdded] = useState(false);

  const [hasMultipleVariants, setHasMultipleVariant] = useState(false);
  const [isRangeExist, setIsRangeExist] = useState(false);
  const [showVariantSelector, setShowVariantSelector] = useState(false);

  const getIsSelected = optionId =>
    selectedVDOption && selectedVDOption.id === optionId;

  const getIfMoreVariantsAddable = () => {
    if (!selectedVDOption) return false;
    if (selectedVDOptionIndex < 0) return false;
    if (!isRangeExist && selectedVariants.length >= selectedVDOption.minValue) {
      return false;
    }
    if (isRangeExist && selectedVariants.length >= selectedVDOption.maxValue) {
      return false;
    }

    return true;
  };

  const getIfVariantRemovable = count => {
    if (!selectedVDOption) return false;
    if (selectedVDOptionIndex < 0) return false;
    if (!isRangeExist) return false;

    if (isRangeExist && selectedVDOption.minValue > count) {
      return false;
    }

    return true;
  };

  const onChangeSelectedVDOption = index => {
    if (selectedVDOptionIndex === index) {
      return setSelectedVDOptionIndex(-1);
    }
    return setSelectedVDOptionIndex(index);
  };

  const getVDShowVariantSelector = () => {
    if (!selectedVDOption) return false;
    if (selectedVDOptionIndex < 0) return false;
    if (selectedVariants.length <= 0) return false;

    if (vdShowVariantSelector === 'none') {
      return false;
    }

    if (vdShowVariantSelector === 'none') {
      return false;
    }

    return true;
  };

  const onChangeVariant = (e, index) => {
    const { value } = e.target;
    if (selectedVDOptionIndex < 0) return;
    const { variants } = selectedVDOption;

    const newVariant = variants.find(variant => variant.title === value);

    let newSelectedVariants = [...selectedVariants];

    if (isVDMultipleVariantsSelectable) {
      newSelectedVariants.splice(index, 1, newVariant);
    } else {
      newSelectedVariants = Array(parseInt(selectedVariants.length, 10)).fill(
        newVariant,
      );
    }

    setSelectedVariants(newSelectedVariants);
  };

  const onRemoveSelectedVariant = (e, index) => {
    const indexToRemove = index || selectedVariants.length - 1;

    if (getIfVariantRemovable(indexToRemove)) {
      setSelectedVariants(
        selectedVariants.filter((_, i) => i !== indexToRemove),
      );
    }
  };

  const onAddNewVariant = () => {
    if (selectedVDOptionIndex < 0) return;

    if (getIfMoreVariantsAddable()) {
      setSelectedVariants([
        ...selectedVariants,
        Array.isArray(selectedVariants) && selectedVariants.length > 0
          ? selectedVariants[selectedVariants.length - 1]
          : selectedVDOption.variants[0],
      ]);
    }
  };

  const onAddClick = async () => {
    if (isAdmin) return;

    setIsATCLoading(true);
    const totalValues = getTotal();
    const variantsToAdd = await getVDVariantsToAdd({
      productId: primary.sp_product_id,
      selectedVariants,
      isDiscountDisabled: !totalValues.displayDiscount,
      discountQuantity: 1,
      isDiscountQuantityRestricted: true,
      theme: props.theme,
      offerType: 'vd',
      isSkipIfAlreadyInCart: true,
    });

    if (variantsToAdd.length > 0) {
      await addOfferedVariantsToCart(variantsToAdd);
      for (let i = 0; i < variantsToAdd.length; i += 1) {
        // eslint-disable-next-line
        const { product_id, variant_id } = variantsToAdd[i];
        addToCartSuccessEvent({
          mrOfferID,
          product_id,
          variant_id,
          isEmbedded: true,
          isProductPage: true,
          theme: props.theme,
          offerType: 'vd',
          widgetID,
        });
      }
    }
    setIsATCLoading(false);
    setIsProductAdded(true);

    setTimeout(() => {
      setIsProductAdded(false);
    }, 4000);

    if (isProductButtonActionGoToCart) {
      goToCart({ isPopup: false, isRefreshPageAfterProductAddition });
      if (isRefreshPageAfterProductAddition) {
        return;
      }
    }

    if (isRefreshPageAfterProductAddition) {
      logger.log('refresh initiated');
      reloadPage();
    }
  };

  const getTotal = () => {
    if (selectedVariants.length <= 0)
      return {
        priceFloat: 0,
        offerPriceFloat: 0,
        discountFloat: 0,
        displayDiscount: false,
      };

    const calculatedTotal = selectedVariants.reduce(
      (acc, curr) => {
        acc.priceFloat += curr.priceFloat;
        acc.offerPriceFloat += roundOff(curr.offerPriceFloat, priceRoundType);
        acc.discountFloat += roundOff(
          curr.priceFloat - curr.offerPriceFloat,
          discountRoundType,
        );
        acc.displayDiscount =
          acc.displayDiscount || curr.calculatedDiscountFloat > 0;

        return acc;
      },
      {
        priceFloat: 0,
        offerPriceFloat: 0,
        discountFloat: 0,
        displayDiscount: false,
      },
    );

    return calculatedTotal;
  };

  const getTotalDisplayValues = newTotal => {
    if (selectedVDOptionIndex < 0 || selectedVariants.length <= 0)
      return {
        totalPrice: 'XX.XX',
        offerPrice: 'XX.XX',
        discount: 'XX.XX',
        displayDiscount: true,
      };

    let totalPrice = getAmountDisplay({
      currencyFormat,
      currencySymbol,
      amount: newTotal.priceFloat,
      decimalSymbol,
      thousandSeparator,
    });

    if (isAdmin) {
      totalPrice = totalPrice.replace(
        roundTo2String(newTotal.priceFloat),
        'XX.XX',
      );
    }

    let offerPrice = getAmountDisplay({
      currencyFormat,
      currencySymbol,
      amount: newTotal.offerPriceFloat,
      decimalSymbol,
      thousandSeparator,
    });

    if (isAdmin) {
      offerPrice = offerPrice.replace(
        roundTo2String(newTotal.offerPriceFloat),
        'XX.XX',
      );
    }

    const discountValue = calculateDiscountValue({
      price: newTotal.priceFloat,
      offerPrice: newTotal.offerPriceFloat,
      discountType,
    });

    let discount = getDiscountDisplay({
      discount: newTotal.discountFloat,
      discountValue: roundOff(discountValue, discountRoundType),
      discountType,
      currencyFormat,
      currencySymbol,
      productDiscountText,
    });

    // for percentage we can use the discount value directly instead of computing
    // based on price and offer price. (due to rounding the values can be off by 0.0x)
    if (discountType === 'percentage') {
      discount = selectedVariants[0].off;
    }

    if (isAdmin) {
      discount = 'XX.XX';
    }

    return {
      totalPrice,
      offerPrice,
      discount,
      displayDiscount: newTotal.displayDiscount,
    };
  };

  useEffect(
    () => {
      // check if an option is selected and update selected option related states
      if (selectedVDOptionIndex >= 0) {
        const {
          conditionType,
          minValue: minVariantsRequired,
          variants,
        } = selectedVDOption;
        // selected Variants can be less or more
        setIsRangeExist(conditionType !== 'equal_to');
        // primary product has multiple variants
        setHasMultipleVariant(
          variants.length > 0 && variants[0].title !== 'Default Title',
        );
        // set minimum variants required to avail the selece vd option discount
        setSelectedVariants(
          Array(parseInt(minVariantsRequired, 10)).fill(variants[0]),
        );
        // update in redux state
        updateSelectedVDOption({
          updatedSelectedVDOption: selectedVDOption,
        });
      } else {
        // set the first option so that the prices are displayed
        const { minValue: minVariantsRequired, variants } = vdOptions[0];
        // set minimum variants required to avail the selece vd option discount
        setSelectedVariants(
          Array(parseInt(minVariantsRequired, 10)).fill(variants[0]),
        );
        // update in redux state
        updateSelectedVDOption({
          updatedSelectedVDOption: null,
        });
      }
    },
    [selectedVDOptionIndex, primary],
  );

  useEffect(
    () => {
      // check if selected variants are added and update discount total values
      if (selectedVariants.length > 0) {
        const newTotal = getTotal();
        // update in redux state
        setTotalDiplayValues(getTotalDisplayValues(newTotal));
      }
    },
    [JSON.stringify(selectedVariants), selectedVDOptionIndex],
  );

  useEffect(
    () => {
      const updatedShowVariantSelectorValue = getVDShowVariantSelector();
      // update in redux state, only for above_atc
      updateIsVDVariantSelectorInViewForAboveATC({
        updatedIsVDVariantSelectorInViewForAboveATC:
          isWidgetPositionAboveATC && updatedShowVariantSelectorValue,
      });
      // update if show variant selection box
      setShowVariantSelector(updatedShowVariantSelectorValue);
    },
    [selectedVDOptionIndex, selectedVariants.length, vdShowVariantSelector],
  );

  useEffect(
    () => {
      // set default selected option in the offer
      let defaultSelectedVDOptionIndex = getDefaultSelectedVDOptionIndex();
      if (selectedVDOptionIndex >= 0 && vdOptions[selectedVDOptionIndex]) {
        defaultSelectedVDOptionIndex = selectedVDOptionIndex;
      }
      // update in redux state
      if (isVDQuantityOptionSelected)
        setSelectedVDOptionIndex(defaultSelectedVDOptionIndex);
      updateVDOfferTheme({
        updatedTheme: props.theme,
      });
    },
    [JSON.stringify(vdOptions)],
  );

  const clearSelectedVDOptionFromRedux = () => {
    setSelectedVDOptionIndex(-1);
    updateSelectedVDOption({ updatedSelectedVDOption: null });
  };

  // clear selected VDOption on unmount
  useEffect(() => () => clearSelectedVDOptionFromRedux(), []);

  const variantOptions = selectedVDOption
    ? selectedVDOption.variants.map(variant => variant.title)
    : [];

  // eslint-disable-next-line
  const variantSelectionSectionTitle = !hasMultipleVariants
    ? vdQuantitySelectorTitleText
    : isVDMultipleVariantsSelectable
      ? vdVariantSecTitleTextForMultiple
      : vdVariantSecTitleText;

  const showVariantPriceAtTop =
    selectedVDOption &&
    (selectedVDOption.isVariantPricesEqual || // show price at top if variant price are equal irrespective of isVDMultipleVariantsSelectable
      (!selectedVDOption.isVariantPricesEqual &&
        !isVDMultipleVariantsSelectable)) && // if variant price are not equal then show price at top only if isVDMultipleVariantsSelectable is not allowed
    vdShowPriceForEachItem;

  const showVariantPriceIndividually =
    selectedVDOption &&
    !selectedVDOption.isVariantPricesEqual &&
    vdShowPriceForEachItem;

  const commonProps = {
    isMobile,
    vdOptions,
    variantOptions,
    selectedVariants,
    isATCLoading,
    selectedVDOptionIndex,
    isProductAdded,
    totalDisplayValues,
    showVariantSelectorSection: showVariantSelector && hasMultipleVariants,
    variantSelectionSectionTitle,
    showVariantPriceAtTop,
    showVariantPriceIndividually,
    showMultipleVariantSelector:
      isVDMultipleVariantsSelectable && hasMultipleVariants,
    showVariantSelectorOnly: hasMultipleVariants,
    showFooter: selectedVDOption !== undefined,
    showATCButton: !isWidgetPositionAboveATC,
    priceDetailStyles: getPriceDetailStyles(isMobile, props.theme),
    getIsSelected,
    getIfMoreVariantsAddable,
    getIfVariantRemovable,
    onChangeSelectedVDOption,
    onChangeVariant,
    onAddNewVariant,
    onRemoveSelectedVariant,
    onAddClick,
    currencyFormat,
    currencySymbol,
    discountType,
    isAdmin,
    ...restProps,
  };

  if (isMobile) {
    if (mobileLayoutType === 'tiles') {
      return <TileLayout {...commonProps} />;
    }
  }
  if (desktopLayoutType === 'tiles') {
    return <TileLayout {...commonProps} />;
  }
}

VD.propTypes = {
  mrOfferID: PropTypes.string,
  primary: PropTypes.object,
  vdOptions: PropTypes.array,
  theme: PropTypes.object,
  isVDMultipleVariantsSelectable: PropTypes.bool,
  currencyFormat: PropTypes.string,
  currencySymbol: PropTypes.string,
  decimalSymbol: PropTypes.string,
  thousandSeparator: PropTypes.string,
  isAdmin: PropTypes.bool,
  isAdminMobileTab: PropTypes.bool,
  getVDVariantsToAdd: PropTypes.func,
  addOfferedVariantsToCart: PropTypes.func,
  goToCart: PropTypes.func,
  addToCartSuccessEvent: PropTypes.func,
  priceRoundType: PropTypes.string,
  discountRoundType: PropTypes.string,
  selectedVariants: PropTypes.array.isRequired,
  setSelectedVariants: PropTypes.func.isRequired,
  selectedVDOptionIndex: PropTypes.number.isRequired,
  setSelectedVDOptionIndex: PropTypes.func.isRequired,
  totalDisplayValues: PropTypes.object.isRequired,
  setTotalDiplayValues: PropTypes.func.isRequired,
  updateIsVDVariantSelectorInViewForAboveATC: PropTypes.func.isRequired,
  updateSelectedVDOption: PropTypes.func,
  updateVDOfferTheme: PropTypes.func,
  discountType: PropTypes.string,
  isVDQuantityOptionSelected: PropTypes.bool,
  vdSelectedQuantityOptionIndex: PropTypes.number,
  widgetID: PropTypes.string,
};
